/**
 * Shared types for the self-service visualization feature.
 *
 * Extracted verbatim from self-service-visualization.component.ts as the first
 * step of splitting that monolith into per-stage components. Keeping these in
 * one place lets the data-builder, chart-builder and dashboard-builder
 * components (plus the shared services) share a single source of truth without
 * importing from each other.
 */
import { EntityFilterPayload } from './self-service-dataset.service';

// Re-exported so dependents can import every self-service type from this module.
export type { EntityFilterPayload } from './self-service-dataset.service';

export type SupportedChartType = string;
export type ColumnType = 'string' | 'number' | 'date' | 'boolean' | 'empty';
export type MappingKey =
  | 'xAxis'
  | 'yAxis'
  | 'category'
  | 'value'
  | 'xAxis2'
  | 'yAxis2'
  | 'size'
  | 'colorBy';
export type SortDirection = 'asc' | 'desc';
export type TopNDirection = 'top' | 'bottom';
export type DateGranularity = 'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export type SupportedFileType = 'csv' | 'excel' | 'entity';
export type ChartPreviewKind =
  | 'axis-bar'
  | 'axis-line'
  | 'step-line'
  | 'horizontal-bar'
  | 'area'
  | 'stacked-bar'
  | 'stacked-horizontal-bar'
  | 'stacked-line'
  | 'stacked-area'
  | 'bar-line'
  | 'pie'
  | 'donut'
  | 'treemap'
  | 'scatter'
  | 'bubble'
  | 'quadrant-bubble'
  | 'waterfall'
  | 'waterfall-stack'
  | 'gantt'
  | 'kpi'
  | 'table'
  | 'generic-drill-down'
  // Interactive widget kinds — these render plain Angular/Material widgets via
  // SelfServiceWidgetComponent rather than ECharts. They are data-less (no
  // mapping required); their settings live in the saved chart's `config` field.
  | 'input-box'
  | 'slider'
  | 'slider-range'
  | 'range-bidirectional-slider'
  | 'configurable-form'
  | 'editable-table'
  | 'button'
  | 'notes'
  | 'upload-file';

/**
 * The preview kinds that are interactive widgets (not ECharts charts).
 * Used to discriminate the widget rendering path in the builder preview, the
 * dashboard-builder cards and the persistence layer (widgets skip ssChartMeta
 * exactly like 'kpi'/'table', and carry their settings via `config`).
 */
export const WIDGET_PREVIEW_KINDS = [
  'input-box',
  'slider',
  'slider-range',
  'range-bidirectional-slider',
  'configurable-form',
  'editable-table',
  'button',
  'notes',
  'upload-file',
] as const;

export type WidgetPreviewKind = (typeof WIDGET_PREVIEW_KINDS)[number];

/** True when a preview kind is one of the interactive widget kinds. */
export function isWidgetPreviewKind(kind: ChartPreviewKind | string | undefined): kind is WidgetPreviewKind {
  return !!kind && (WIDGET_PREVIEW_KINDS as readonly string[]).includes(kind);
}
export type NumberFormat = 'plain' | 'thousands' | 'percent' | 'currency';
/** Magnitude abbreviation applied on top of `NumberFormat`. 'compact' picks
 *  K/M/B automatically based on the value's magnitude; 'millions'/'billions'
 *  force a fixed divisor regardless of magnitude. */
export type NumberScale = 'none' | 'compact' | 'millions' | 'billions';
/** Line series shape: 'curved' = smooth bézier, 'straight' = polyline. */
export type LineShape = 'curved' | 'straight';
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right';
export type LabelMode = 'value' | 'percent' | 'name-value' | 'name-percent';
export type AggregationType =
  | 'sum'
  | 'avg'
  | 'count'
  | 'distinctCount'
  | 'min'
  | 'max'
  | 'median'
  | 'range'
  | 'first'
  | 'last'
  | string;

export interface DatasetColumn {
  name: string;
  type: ColumnType;
}

export interface ChartTypeOption {
  label: string;
  value: SupportedChartType;
  requiredMappings: MappingKey[];
  optionalMappings?: MappingKey[];
  previewKind: ChartPreviewKind;
  supportsAggregation: boolean;
  supportsSort?: boolean;
  supportsTopN?: boolean;
  supportsDateGranularity?: boolean;
  supportsTooltipColumns?: boolean;
  supportsComparison?: boolean;
  supportsReferenceLine?: boolean;
}

export interface UploadedDataset {
  datasetId?: string;
  fileName: string;
  sourceType: SupportedFileType;
  columns: DatasetColumn[];
  rows: Record<string, string>[];
}

export interface ChartShapeOptions {
  sortBy?: string;
  sortDir?: SortDirection;
  topN?: number | null;
  topNDirection?: TopNDirection;
  dateGranularity?: DateGranularity;
  tooltipColumns?: string[];
  referenceValue?: number | null;
}

export type TooltipTrigger = 'auto' | 'axis' | 'item';
export type TooltipAxisPointer = 'none' | 'line' | 'cross' | 'shadow';

export interface ChartStyle {
  titleOverride: string;
  showLegend: boolean;
  legendPosition: LegendPosition;
  showLabels: boolean;
  labelMode: LabelMode;
  palette: string;
  xAxisLabel: string;
  yAxisLabel: string;
  xAxisLabel2: string;
  yAxisLabel2: string;
  quadrantTopLeftLabel?: string;
  quadrantTopRightLabel?: string;
  quadrantBottomLeftLabel?: string;
  quadrantBottomRightLabel?: string;
  numberFormat: NumberFormat;
  // Magnitude scale (K/M/B) applied alongside numberFormat. Optional so
  // existing saved charts (no scale chosen) keep their current look.
  numberScale?: NumberScale;
  decimals: number;
  // Secondary (right) Y-axis number format + decimals. When unset, the chart
  // falls back to the primary numberFormat/decimals so existing saved charts
  // keep their current look. Lets a dual-axis chart format each axis (and the
  // tooltip values that belong to each axis) independently.
  numberFormat2?: NumberFormat;
  numberScale2?: NumberScale;
  decimals2?: number;
  // Line series shape (line / area / bar-line combo line). Defaults to 'curved'
  // to preserve the prior hardcoded smooth:true behaviour.
  lineShape?: LineShape;
  comparisonValue: number | null;
  tableRowLimit: number;
  tooltipEnabled: boolean;
  tooltipTrigger: TooltipTrigger;
  tooltipBackground: string;
  tooltipTextColor: string;
  tooltipBorderColor: string;
  tooltipBorderRadius: number;
  tooltipFontSize: number;
  tooltipAxisPointer: TooltipAxisPointer;
  // Round-off for TOOLTIP values only — decimal places shown in the hover
  // tooltip, independent of the axis decimals. When unset, tooltips fall back to
  // the axis decimals (primary/secondary) so existing charts are unchanged.
  tooltipDecimals?: number;
}

/** One field in a Configurable Form widget. */
export interface WidgetFormField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'tel' | 'textarea' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

/** One column in an Editable Table widget. */
export interface WidgetTableColumn {
  key: string;
  label: string;
  type?: 'text' | 'number';
}

/**
 * Settings for an interactive widget chart type. Stored on the saved chart's
 * `config` field. Every property is optional — the widget render component
 * applies sensible defaults so a freshly-picked widget previews immediately.
 */
export interface WidgetConfig {
  /** Common */
  label?: string;
  placeholder?: string;
  defaultValue?: string | number;
  /** Slider / range-slider / bidirectional slider */
  min?: number;
  max?: number;
  step?: number;
  rangeStart?: number;
  rangeEnd?: number;
  /** Button */
  buttonLabel?: string;
  buttonStyle?: 'primary' | 'secondary' | 'stroked';
  /** Notes */
  notesText?: string;
  /** Configurable form */
  submitLabel?: string;
  fields?: WidgetFormField[];
  /** Editable table */
  columns?: WidgetTableColumn[];
  rows?: Array<Record<string, string>>;
  /** Upload file */
  accept?: string;
  multiple?: boolean;
  /** Names of files the user selected in the builder (the File objects
   *  themselves are not persisted — actual upload happens in the published
   *  view via the production UploadFileComponent). */
  uploadedFileNames?: string[];
}

export interface SavedSelfServiceChart {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  datasetId?: string;
  chartType: SupportedChartType;
  aggregation: AggregationType;
  mapping: Partial<Record<MappingKey, string>>;
  seriesField?: string;
  sortBy?: string;
  sortDir?: SortDirection;
  topN?: number | null;
  topNDirection?: TopNDirection;
  dateGranularity?: DateGranularity;
  tooltipColumns?: string[];
  comparisonColumn?: string;
  referenceValue?: number | null;
  style?: ChartStyle;
  filterKey: string;
  filterListener: string[];
  filterListenerCustom?: string[];
  dataset: UploadedDataset;
  config: Record<string, unknown>;
  /** Settings for interactive widget chart types (input-box, slider, etc.).
   *  Undefined for ordinary data charts. */
  widgetConfig?: WidgetConfig;
}

export interface DashboardDraftItem {
  id: string;
  chartId: string;
  addedAt: string;
  colSpan?: number;
  filterKeyOverride?: string;
  listenerItemIds?: string[];
  /** Visible data-table columns for this dashboard card, in display order. */
  tableColumns?: string[];
  /** Full data-table column order for this dashboard card. */
  tableColumnOrder?: string[];
  /** Tab this chart belongs to (tabbed dashboards only). Absent / empty means
   *  the item is part of the single-page dashboard (backward compatible). */
  tabId?: string;
}

export interface ActiveCrossFilter {
  sourceChartId: string;
  sourceItemId: string;
  filterKey: string;
  filterValues: string[];
}

export interface GlobalFilter {
  key: string;
  label: string;
  isMultiSelect: boolean;
  parentKey?: string;
}

export interface DashboardLayout {
  columns: number;
  /** DVM published-dashboard card order/sizing metadata. */
  items?: Array<Record<string, unknown>>;
  /** Full row/column widget layout used by the published renderer. */
  pageContent?: unknown;
}

export type DashboardStatus = 'draft' | 'published';
export type GlobalFiltersPosition = 'top' | 'bottom' | 'left' | 'right';

/** A tab in a tabbed self-service dashboard. Charts are assigned to a tab via
 *  their `tabId`; each tab may optionally carry its own local filters. */
export interface DashboardTab {
  id: string;
  label: string;
  /** Optional per-tab (local) filters, in addition to the dashboard-level
   *  global filters. Phase 2 authoring; the shape mirrors GlobalFilter. */
  filters?: GlobalFilter[];
}

export interface DashboardDraftState {
  name: string;
  items: DashboardDraftItem[];
  globalFilters?: GlobalFilter[];
  globalFiltersPosition?: GlobalFiltersPosition;
  layout?: DashboardLayout;
  /** Present only for tabbed dashboards. */
  tabs?: DashboardTab[];
}

export interface SavedSelfServiceDashboard {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  items: DashboardDraftItem[];
  globalFilters?: GlobalFilter[];
  globalFiltersPosition?: GlobalFiltersPosition;
  layout?: DashboardLayout;
  status?: DashboardStatus;
  publishedAt?: string;
  config: Record<string, unknown>;
  /** Present only for tabbed dashboards. */
  tabs?: DashboardTab[];
}

export interface DashboardCard {
  item: DashboardDraftItem;
  chart: SavedSelfServiceChart;
}

export interface ComputedColumn {
  name: string;
  expression: string;
}

export interface CustomAggregation {
  key: string;
  label: string;
  expression: string;
}

export interface EntityFormulaDefinition {
  name: string;
  expression: string;
}

export interface EntityFilterDefinition extends EntityFilterPayload {}

export interface ComputationVersion {
  expression: string;
  label?: string;
  savedAt: string;
}

export interface LibraryComputation {
  id: string;
  kind: 'column' | 'aggregation';
  name: string;
  label?: string;
  expression: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  versions: ComputationVersion[];
}

export interface FormulaTemplate {
  id: string;
  label: string;
  kind: 'column' | 'aggregation';
  category: 'Sum' | 'Average' | 'Count' | 'Percentage' | 'Ratio' | 'Derived';
  description: string;
  defaultName: string;
  expressionTemplate: string;
}

export interface DatasetFormulas {
  computedColumns: ComputedColumn[];
  rowFilter: string;
  customAggregations: CustomAggregation[];
}



